import { useMutation, useQuery } from '@tanstack/react-query'
import { parseCookies } from 'nookies'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { getCampaignsById } from '@/api/campaigns/get-campaign-by-id'
import { finalizarTituloPremiado } from '@/api/campaigns/titulo'
import { createPayment, type Payer } from '@/api/payments/create-payment'
import { getPaymentByExternalId } from '@/api/payments/get-payment'
import { getProfile } from '@/api/user/get-profile'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { env } from '@/env'
import { useTicketHub } from '@/hooks/useTicketHub'
import { useHeadManager } from '@/lib/headManagerProvider'
import { api } from '@/services/axios'

import type { AwardedTitle } from './types'

const PaymentResponseSchema = z.object({
  id: z.number(),
  pointOfInteraction: z.object({
    transactionData: z.object({
      qrCode: z.string(),
      qrCodeBase64: z.string(),
    }),
  }),
})

const PAYMENT_CHECK_INTERVAL = 2000

export function Checkout() {
  const navigate = useNavigate()
  const { setTitle } = useHeadManager()
  const { rifaId } = useParams<{ rifaId: string }>()

  // Parse cookies
  const cookies = parseCookies()
  const userId = cookies['reactAuth.id'] || ''
  const email = cookies['reactAuth.email'] || ''

  // Ticket Hub State
  const {
    blockedNumbers = [],
    paidNumbers = [],
    pendingNumbers = [],
    numbersLoaded = false,
    selectNumber = async () => {},
    unselectNumber = async () => {},
    confirmPayment = async () => {},
    markPendingPayment = async () => {},
    error: hubError,
  } = useTicketHub(rifaId ? Number(rifaId) : 0)

  // Component State
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([])
  const [randomCount, setRandomCount] = useState(0)
  const [paymentCode, setPaymentCode] = useState('')
  const [qrCodeBase64, setQrCodeBase64] = useState('')
  const [copied, setCopied] = useState(false)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [total, setTotal] = useState(0)
  const [discountMessage, setDiscountMessage] = useState('')
  const [loadingNumbers, setLoadingNumbers] = useState<number[]>([])

  // Data Fetching
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['profile', email],
    queryFn: () => getProfile({ email }),
    enabled: !!email,
    staleTime: Infinity,
  })

  const {
    data: rifaSelecionada,
    mutate: fetchRifa,
    isPending: isLoadingRifa,
  } = useMutation({
    mutationFn: (id: number) => getCampaignsById(id),
  })

  // Payment Mutation
  const { mutate: gerarPagamento, isPending: isGeneratingPayment } =
    useMutation({
      mutationFn: (data: Payer) => createPayment(data),
      onSuccess: async (data: unknown) => {
        try {
          const parsedData = PaymentResponseSchema.parse(data)
          setPaymentCode(parsedData.pointOfInteraction.transactionData.qrCode)
          setQrCodeBase64(
            parsedData.pointOfInteraction.transactionData.qrCodeBase64,
          )

          await markPendingPayment(selectedNumbers)

          setSelectedNumbers([])

          setIsPaymentModalOpen(true)
          toast.success('Pagamento gerado com sucesso!')
          consultarStatusPagamento(parsedData.id.toString())
        } catch (error) {
          toast.error('Erro ao processar resposta do pagamento')
          console.error('Validation error:', error)
        }
      },
      onError: () => {
        toast.error('Erro ao gerar pagamento. Tente novamente.')
      },
    })

  // Effects
  useEffect(() => {
    if (rifaId) {
      fetchRifa(Number(rifaId))
      setTitle('Pagamento')
    }
  }, [rifaId, fetchRifa, setTitle])

  useEffect(() => {
    if (selectedNumbers.length > 0 && rifaSelecionada) {
      const { total: calculatedTotal, message } = calculateTotal()
      setTotal(calculatedTotal)
      setDiscountMessage(message)
    } else {
      setTotal(0)
      setDiscountMessage('')
    }
  }, [selectedNumbers, rifaSelecionada])

  // Memoized values
  const isSelectionValid = useMemo(() => {
    if (!rifaSelecionada?.campanha) return false
    return (
      selectedNumbers.length >= rifaSelecionada.campanha.titulo_Min &&
      selectedNumbers.length <= rifaSelecionada.campanha.titulo_Max
    )
  }, [selectedNumbers, rifaSelecionada])

  // Callbacks
  const calculateTotal = useCallback(() => {
    if (!rifaSelecionada?.campanha) return { total: 0, message: '' }

    const valorUnitario = rifaSelecionada.campanha.titulo_Valor || 0
    const total = selectedNumbers.length * valorUnitario
    let totalComPromocao = total
    let message = ''

    if (rifaSelecionada.promocoes?.length > 0) {
      const promocoesOrdenadas = [...rifaSelecionada.promocoes].sort(
        (a, b) => b.quantidade - a.quantidade,
      )

      let numerosRestantes = selectedNumbers.length
      totalComPromocao = 0
      const appliedPromos = []

      for (const promo of promocoesOrdenadas) {
        while (numerosRestantes >= promo.quantidade) {
          const grupos = Math.floor(numerosRestantes / promo.quantidade)
          if (grupos > 0) {
            totalComPromocao += promo.valor * grupos
            numerosRestantes -= promo.quantidade * grupos
            appliedPromos.push(
              `${grupos}x ${promo.quantidade} números por R$ ${promo.valor.toFixed(2).replace('.', ',')}`,
            )
          }
        }
      }

      totalComPromocao += numerosRestantes * valorUnitario

      if (appliedPromos.length > 0) {
        message = `Promoções aplicadas:\n${appliedPromos.join('\n')}`
        if (numerosRestantes > 0) {
          message += `\n${numerosRestantes} números pelo valor normal de R$ ${valorUnitario.toFixed(2).replace('.', ',')}`
        }
        const economia = total - totalComPromocao
        message += `\nEconomia total: R$ ${economia.toFixed(2).replace('.', ',')}`
      }
    }

    return {
      total: Math.min(total, totalComPromocao),
      message,
    }
  }, [selectedNumbers, rifaSelecionada])

  async function fetchAwardedTitles(rifaId?: string): Promise<AwardedTitle[]> {
    try {
      const response = await api.get<AwardedTitle[]>(
        `CampanhaTituloPremiado/GetAll`,
        {
          params: { campanhaId: rifaId },
        },
      )
      return response.data
    } catch (error) {
      console.error('Erro ao buscar títulos premiados:', error)
      return []
    }
  }

  const consultarStatusPagamento = useCallback(
    async (externalId: string) => {
      const checkPayment = async () => {
        try {
          const paymentDetails = await getPaymentByExternalId(
            Number(externalId),
          )
          const status = paymentDetails?.status?.toLowerCase()

          if (status === 'approved') {
            await confirmPayment(selectedNumbers)
            setIsPaymentModalOpen(false)

            const awardedTitles = await fetchAwardedTitles(rifaId)

            const wonTitles = awardedTitles.filter((awarded) =>
              selectedNumbers.includes(Number(awarded.titulo)),
            )

            for (const titulo of wonTitles) {
              const { data: userId } = await api.get<number>(
                `CampanhaPremio/GetUserPurchasedNumber/${titulo.id}`,
              )

              await finalizarTituloPremiado(titulo.id, userId)
            }

            navigate(`/campanhas/${rifaId}/pagamento-concluido`, {
              state: { wonTitles },
            })

            return true
          } else if (status !== 'pending') {
            toast.warning('Verifique seus pagamentos pendentes.')
            return true
          }
        } catch (error) {
          console.error('Erro ao consultar pagamento:', error)
        }

        return false
      }

      const intervalId = setInterval(async () => {
        const shouldStop = await checkPayment()
        if (shouldStop) {
          clearInterval(intervalId)
        }
      }, PAYMENT_CHECK_INTERVAL)

      return () => clearInterval(intervalId)
    },
    [confirmPayment, navigate, rifaId, selectedNumbers],
  )

  const toggleNumber = useCallback(
    async (num: number) => {
      if (!rifaSelecionada?.campanha) {
        toast.error('Dados da rifa não carregados')
        return
      }

      try {
        if (selectedNumbers.includes(num)) {
          await unselectNumber(num)
          setSelectedNumbers((prev) => prev.filter((n) => n !== num))
          toast.success(`Número ${num} removido`)
        } else {
          if (
            selectedNumbers.length >=
            (rifaSelecionada.campanha.titulo_Max ?? Infinity)
          ) {
            toast.error(
              `Limite de ${rifaSelecionada.campanha.titulo_Max} números atingido`,
            )
            return
          }

          if (blockedNumbers.includes(num)) {
            toast.error(`Número ${num} já está selecionado por outro usuário`)
            return
          }

          setLoadingNumbers((prev) => [...prev, num])

          try {
            await selectNumber(num)
            setSelectedNumbers((prev) => [...prev, num])
            toast.success(`Número ${num} selecionado`)
          } catch {
            toast.error(`Erro ao selecionar número ${num}`)
          } finally {
            setLoadingNumbers((prev) => prev.filter((n) => n !== num))
          }
        }
      } catch (error) {
        toast.error('Falha ao alternar número')
        console.error(error)
      }
    },
    [
      rifaSelecionada,
      selectedNumbers,
      blockedNumbers,
      selectNumber,
      unselectNumber,
    ],
  )

  const clearAllNumbers = useCallback(async () => {
    try {
      await Promise.all(
        selectedNumbers.map((num) => unselectNumber(num).catch(console.error)),
      )
      setSelectedNumbers([])
      toast.success('Todos os números foram removidos')
    } catch (error) {
      toast.error('Falha ao remover números')
      console.error(error)
    }
  }, [selectedNumbers, unselectNumber])

  const handleGeneratePayment = useCallback(() => {
    if (!rifaSelecionada || !email) {
      toast.error('Dados incompletos para gerar pagamento')
      return
    }

    if (selectedNumbers.length === 0) {
      toast.info('Selecione pelo menos um número.')
      return
    }

    const paymentData: Payer = {
      NomeCompleto: profile?.nome ?? '',
      Email: profile?.email ?? '',
      Telefone: profile?.whatsapp ?? '',
      Descricao: `Compra de ${selectedNumbers.length} números na rifa ${rifaSelecionada.campanha.titulo}`,
      Valor: total,
      CampanhaId: rifaSelecionada.campanha.id,
      TitulosComprados: selectedNumbers,
      UsuarioId: Number(userId),
    }

    gerarPagamento(paymentData)
  }, [
    rifaSelecionada,
    email,
    selectedNumbers,
    profile,
    total,
    userId,
    gerarPagamento,
  ])

  const handleRandomSelection = useCallback(async () => {
    if (!rifaSelecionada?.campanha) {
      toast.error('Dados da rifa não carregados')
      return
    }

    const totalNumbers = rifaSelecionada.campanha.titulo_Quantidade ?? 0
    const availableNumbers = Array.from(
      { length: totalNumbers },
      (_, i) => i + 1,
    ).filter(
      (num) =>
        !blockedNumbers.includes(num) &&
        !paidNumbers.includes(num) &&
        !pendingNumbers.includes(num),
    )

    if (randomCount <= 0 || randomCount > availableNumbers.length) {
      toast.error('Quantidade inválida!')
      return
    }

    const shuffled = [...availableNumbers].sort(() => 0.5 - Math.random())
    const randomNumbers = shuffled.slice(0, randomCount)

    try {
      await Promise.all(
        selectedNumbers.map((num) => unselectNumber(num).catch(console.error)),
      )

      await Promise.all(
        randomNumbers.map((num) => selectNumber(num).catch(console.error)),
      )

      setSelectedNumbers(randomNumbers)
      toast.success(`${randomCount} números selecionados!`)
    } catch (error) {
      toast.error('Erro ao selecionar números')
      console.error(error)
    }
  }, [
    rifaSelecionada,
    randomCount,
    blockedNumbers,
    paidNumbers,
    pendingNumbers,
    selectedNumbers,
    selectNumber,
    unselectNumber,
  ])

  const handleCopyCode = useCallback(() => {
    navigator.clipboard.writeText(paymentCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [paymentCode])

  const renderNumberButton = useCallback(
    (num: number) => {
      if (!rifaSelecionada?.campanha) return null

      const isSelected = selectedNumbers.includes(num)
      const isPaid = paidNumbers.includes(num)
      const isPending = pendingNumbers.includes(num)
      const isBlocked = blockedNumbers.includes(num) && !isSelected

      const disabled =
        isPaid || isPending || isBlocked || loadingNumbers.includes(num)

      const buttonClass = [
        'rounded-md border p-2 text-center font-semibold transition',
        isPaid
          ? 'bg-blue-600 text-white'
          : isPending
            ? 'bg-yellow-500 text-white'
            : isSelected
              ? 'bg-green-700 text-white'
              : isBlocked
                ? 'cursor-not-allowed bg-gray-400 text-white'
                : 'bg-muted text-muted-foreground hover:bg-green-100',
      ].join(' ')

      const title = isPaid
        ? 'Número pago'
        : isPending
          ? 'Pagamento pendente'
          : isBlocked
            ? 'Selecionado por outro usuário'
            : 'Disponível para seleção'

      return (
        <button
          key={`number-${num}`}
          onClick={() => toggleNumber(num)}
          disabled={disabled}
          className={buttonClass}
          title={title}
        >
          {num}
        </button>
      )
    },
    [
      paidNumbers,
      pendingNumbers,
      rifaSelecionada,
      selectedNumbers,
      loadingNumbers,
      blockedNumbers,
      toggleNumber,
    ],
  )

  // Loading States
  if (!rifaId) {
    return (
      <div className="p-4 text-center">
        <h2 className="text-2xl font-bold">ID da rifa não encontrado</h2>
        <Button className="mt-4" onClick={() => navigate('/')}>
          Voltar para início
        </Button>
      </div>
    )
  }

  if (isLoadingRifa || isLoadingProfile || !numbersLoaded) {
    return <LoadingSkeleton />
  }

  if (hubError) {
    clearAllNumbers()
    return (
      <div className="p-4 text-center text-red-500">
        <h2 className="text-2xl font-bold">Erro de Conexão</h2>
        <p>Sua conexão foi perdida!!</p>
        <Button className="mt-4" onClick={() => window.location.reload()}>
          Tentar Novamente
        </Button>
      </div>
    )
  }

  if (!rifaSelecionada) {
    return (
      <div className="p-4 text-center">
        <h2 className="text-2xl font-bold">Rifa não encontrada</h2>
        <Button className="mt-4" onClick={() => navigate('/')}>
          Voltar para início
        </Button>
      </div>
    )
  }

  return (
    <div className="p-4">
      <h1 className="text-primary mb-4 text-center text-4xl font-bold">
        Finalizar Pagamento
      </h1>
      <p className="text-muted-foreground mb-8 text-center">
        Escolha seus números para concluir o pagamento.
      </p>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
        {/* Left Column - Campaign Info */}
        <div className="lg:col-span-1">
          <CampaignCard rifaSelecionada={rifaSelecionada} />
        </div>

        {/* Middle Column - Number Selection */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-5 gap-2 sm:grid-cols-10 md:gap-4">
            {Array.from(
              { length: rifaSelecionada.campanha.titulo_Quantidade },
              (_, i) => i + 1,
            ).map(renderNumberButton)}
          </div>

          <NumberLegend />
        </div>

        {/* Right Column - Payment Summary */}
        <div className="space-y-4 lg:col-span-1">
          <PaymentSummaryCard
            rifaSelecionada={rifaSelecionada}
            randomCount={randomCount}
            setRandomCount={setRandomCount}
            handleRandomSelection={handleRandomSelection}
            clearAllNumbers={clearAllNumbers}
            selectedNumbers={selectedNumbers}
            total={total}
            discountMessage={discountMessage}
            isGeneratingPayment={isGeneratingPayment}
            isSelectionValid={isSelectionValid}
            email={email}
            handleGeneratePayment={handleGeneratePayment}
          />
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentDialog
        isOpen={isPaymentModalOpen}
        onOpenChange={setIsPaymentModalOpen}
        qrCodeBase64={qrCodeBase64}
        paymentCode={paymentCode}
        copied={copied}
        handleCopyCode={handleCopyCode}
      />
    </div>
  )
}

// Sub-components
function LoadingSkeleton() {
  return (
    <div className="p-4">
      <Skeleton className="mx-auto mb-4 h-10 w-64" />
      <Skeleton className="mx-auto mb-8 h-6 w-80" />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <Card className="w-full max-w-md overflow-hidden rounded-2xl bg-green-950 text-white shadow-lg">
            <Skeleton className="h-64 w-full" />
            <CardContent className="space-y-4 p-6 text-center">
              <Skeleton className="mx-auto h-8 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="mx-auto h-6 w-1/2" />
              <div className="space-y-2">
                <Skeleton className="mx-auto h-5 w-1/4" />
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <div className="grid grid-cols-5 gap-2 sm:grid-cols-10 md:gap-4">
            {[...Array(100)].map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-md" />
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded-md" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4 lg:col-span-1">
          <Card className="space-y-4 p-4 shadow">
            <div>
              <Skeleton className="mb-2 h-5 w-1/2" />
              <Skeleton className="h-10 w-full" />
              <div className="pt-4">
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-10 w-full" />
          </Card>
        </div>
      </div>
    </div>
  )
}

function CampaignCard({ rifaSelecionada }: { rifaSelecionada: any }) {
  return (
    <Card className="w-full max-w-md overflow-hidden rounded-2xl bg-green-950 text-white shadow-lg">
      <img
        src={`${env.VITE_API_URL}${rifaSelecionada.campanha.imagem}`}
        alt={rifaSelecionada.campanha.titulo}
        className="h-64 w-full object-cover"
        loading="lazy"
      />
      <CardContent className="space-y-4 p-6 text-center">
        <h2 className="text-3xl font-bold">
          {rifaSelecionada.campanha.titulo}
        </h2>
        <p className="text-muted-foreground text-sm">
          {rifaSelecionada.campanha.descricao}
        </p>
        <div className="text-primary text-2xl font-extrabold">
          R${' '}
          {Number(rifaSelecionada.campanha.titulo_Valor)
            .toFixed(2)
            .replace('.', ',')}
        </div>

        {rifaSelecionada.premios.length > 0 && (
          <div className="space-y-1">
            <h3 className="text-md font-semibold text-green-300">🎁 Prêmios</h3>
            <ul className="list-none space-y-1 text-sm">
              {rifaSelecionada.premios.map((premio: any) => (
                <li
                  key={`premio-${premio.id}-${premio.posicao}`}
                  className="text-muted-foreground"
                >
                  <span className="font-medium">{premio.posicao}º lugar</span> -{' '}
                  {premio.descricao}
                </li>
              ))}
            </ul>
          </div>
        )}

        {rifaSelecionada.promocoes.length > 0 && (
          <div className="mt-4 rounded-xl p-4">
            <h3 className="text-md font-semibold text-blue-400">
              🔥 Promoções
            </h3>
            <ul className="mt-2 space-y-1 text-sm">
              {rifaSelecionada.promocoes.map((promo: any) => (
                <li
                  key={`promo-${promo.id}-${promo.quantidade}`}
                  className="font-medium text-white"
                >
                  {promo.quantidade} números por R${' '}
                  {promo.valor.toFixed(2).replace('.', ',')}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function NumberLegend() {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-center gap-4 pt-4 text-center text-sm sm:justify-start sm:text-left">
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded-md bg-gray-400"></div>
        <span>Indisponível</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="bg-muted h-4 w-4 rounded-md"></div>
        <span>Disponível</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded-md bg-green-700"></div>
        <span>Selecionado</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded-md bg-yellow-500"></div>
        <span>Pendente</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded-md bg-blue-600"></div>
        <span>Pago</span>
      </div>
    </div>
  )
}

interface PaymentSummaryCardProps {
  rifaSelecionada: any
  randomCount: number
  setRandomCount: (value: number) => void
  handleRandomSelection: () => void
  clearAllNumbers: () => void
  selectedNumbers: number[]
  total: number
  discountMessage: string
  isGeneratingPayment: boolean
  isSelectionValid: boolean
  email: string
  handleGeneratePayment: () => void
}

function PaymentSummaryCard({
  rifaSelecionada,
  randomCount,
  setRandomCount,
  handleRandomSelection,
  clearAllNumbers,
  selectedNumbers,
  total,
  discountMessage,
  isGeneratingPayment,
  isSelectionValid,
  email,
  handleGeneratePayment,
}: PaymentSummaryCardProps) {
  return (
    <Card className="space-y-4 p-4 shadow">
      {(rifaSelecionada.campanha.modo_Id === 1 ||
        rifaSelecionada.campanha.modo_Id === 3) && (
        <div>
          <label className="text-sm font-medium">
            Quantidade de números aleatórios
          </label>
          <Input
            type="number"
            min="1"
            max={rifaSelecionada.campanha.titulo_Quantidade}
            value={randomCount === 0 ? '' : randomCount}
            onChange={(e) => setRandomCount(Number(e.target.value))}
            placeholder="Digite a quantidade"
          />
          <div className="pt-4">
            <Button
              onClick={handleRandomSelection}
              disabled={
                randomCount <= 0 ||
                randomCount > rifaSelecionada.campanha.titulo_Quantidade ||
                isGeneratingPayment
              }
            >
              Escolher aleatoriamente
            </Button>
          </div>
        </div>
      )}

      <div className="text-muted-foreground text-sm">
        Números selecionados:{' '}
        <span className="font-semibold">{selectedNumbers.length}</span>
      </div>

      {selectedNumbers.length > 0 && (
        <Button
          variant="outline"
          className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          onClick={clearAllNumbers}
          disabled={isGeneratingPayment}
        >
          Limpar Todos
        </Button>
      )}

      <div className="text-lg font-bold">
        Total: R$ {total.toFixed(2).replace('.', ',')}
        {discountMessage && (
          <div className="mt-1 text-sm whitespace-pre-line text-green-600">
            {discountMessage}
          </div>
        )}
      </div>

      <Button
        className="w-full bg-green-600 text-white hover:bg-green-700"
        disabled={!isSelectionValid || isGeneratingPayment || !email}
        onClick={handleGeneratePayment}
      >
        {isGeneratingPayment ? 'Gerando...' : 'Gerar Pagamento'}
      </Button>
    </Card>
  )
}

interface PaymentDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  qrCodeBase64: string
  paymentCode: string
  copied: boolean
  handleCopyCode: () => void
}

function PaymentDialog({
  isOpen,
  onOpenChange,
  qrCodeBase64,
  paymentCode,
  copied,
  handleCopyCode,
}: PaymentDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Pagamento via PIX</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {qrCodeBase64 && (
            <div className="space-y-2 text-center">
              <p className="text-muted-foreground text-sm">
                QR Code para pagamento
              </p>
              <img
                src={`data:image/png;base64,${qrCodeBase64}`}
                alt="QR Code"
                className="mx-auto h-48 w-48"
              />
            </div>
          )}

          {paymentCode && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-green-600">
                Código para pagamento
              </h3>
              <div className="flex items-center gap-2">
                <Input
                  value={paymentCode}
                  readOnly
                  className="flex-1 cursor-text"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyCode}
                >
                  {copied ? 'Copiado!' : 'Copiar'}
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-green-600">
              Como pagar?
            </h3>
            <div className="flex items-start gap-2">
              <div className="font-bold text-green-600">1.</div>
              <p className="text-muted-foreground text-sm">
                Entre no app ou site do seu banco e escolha pagamento via Pix.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="font-bold text-green-600">2.</div>
              <p className="text-muted-foreground text-sm">
                Escaneie o QR Code ou copie o código para pagamento.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <div className="font-bold text-green-600">3.</div>
              <p className="text-muted-foreground text-sm">
                Pronto! Você receberá a confirmação por e-mail.
              </p>
            </div>
            <p className="text-muted-foreground pt-2 text-xs">
              * O Pix pode ter limite diário. Confirme com seu banco.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
